import { Form } from 'react-final-form'

import { DropdownType } from 'common/components/dynamicFields/interfaces'
import { useMoveIntercomButton } from 'common/components/intercom/useMoveIntercomButton'
import Loader from 'common/components/loaders/Loader'
import { isComponentElement } from 'features/unifiedFlow/api/interfaces'
import useGetSplitCalculatorConfigurationQuery from 'features/unifiedFlow/api/queries/useGetSplitCalculatorConfigurationQuery'
import { setFormError } from 'features/unifiedFlow/components/templateSelection/configPanel/TemplateConfigPanel'
import useProjectOptions from 'features/unifiedFlow/components/templateSelection/setupExperiment/hooks/useProjectOptions'
import RightPanelHeading from 'features/unifiedFlow/contentPage/components/layout/RightPanelHeading'
import ExpandedIcons from 'features/unifiedFlow/contentPage/generate/rightSection/ExpandedIcons'
import {
  ActivePanel,
  HandleActivePanelChange,
} from 'features/unifiedFlow/contentPage/generate/rightSection/RightSection'
import { useSelectedElement } from 'features/unifiedFlow/contentPage/hooks'

import ExperimentSettingsFormContent from './ExperimentSettingsFormContent'

type Props = {
  activePanel: ActivePanel | undefined
  handleActivePanelChange: HandleActivePanelChange
}

const ExperimentSettings = ({
  activePanel,
  handleActivePanelChange,
}: Props) => {
  useMoveIntercomButton()

  const { data: selectedElement } = useSelectedElement()
  const optimizationContentType = selectedElement?.optimization_content_type

  const projectOptions = useProjectOptions({ optimizationContentType })

  const optimizationSettings =
    selectedElement && isComponentElement(selectedElement)
      ? selectedElement?.optimization_settings?.configuration
      : undefined

  const selectedProjectId = optimizationSettings?.project_id

  const { data: projectSplitCalculatorConfiguration, isLoading } =
    useGetSplitCalculatorConfigurationQuery({
      projectId: selectedProjectId,
    })

  const splitCalculatorConfiguration =
    projectSplitCalculatorConfiguration?.splitCalculation

  const selectionMetricConfiguration =
    splitCalculatorConfiguration?.fields.find(
      (field) => field.name === 'selection_metric'
    ) as DropdownType

  const initialProject = projectOptions?.find(
    (project) => project.value === selectedProjectId
  )

  const getState = () => {
    if (isLoading) {
      return 'loading'
    } else {
      return 'data'
    }
  }

  const state = getState()

  return (
    <>
      {optimizationSettings && (
        <>
          {
            {
              loading: (
                <div className="flex flex-col h-100 lg:h-full">
                  <Loader />
                </div>
              ),
              data: (
                <div
                  className="flex flex-col h-full text-base-800 p-6"
                  data-testid="advanced-settings-panel"
                >
                  <RightPanelHeading
                    text="Advanced settings"
                    className="pb-10"
                    icons={
                      <ExpandedIcons
                        activePanel={activePanel}
                        handleActivePanelChange={handleActivePanelChange}
                      />
                    }
                  />
                  <Form
                    initialValues={{
                      project: initialProject,
                      experimentName: optimizationSettings.campaign_name,
                      sendDate: optimizationSettings.iso_send_date,
                      controlSubjectLine: optimizationSettings.own_subject_line,
                      listSize:
                        optimizationSettings.split_calculation.list_size,
                      splitNumber:
                        optimizationSettings.split_calculation.configuration
                          ?.split_number,
                      selectionMetric:
                        selectionMetricConfiguration?.options.find(
                          (option) =>
                            option.value ===
                            optimizationSettings.split_calculation
                              .selection_metric
                        ),
                      openRate:
                        optimizationSettings.split_calculation
                          .baseline_open_rate,
                      clickRate:
                        optimizationSettings.split_calculation
                          .baseline_click_rate,
                    }}
                    mutators={{
                      setError: setFormError,
                    }}
                    onSubmit={() => {}}
                    keepDirtyOnReinitialize
                    render={({ handleSubmit }) => (
                      <form
                        id="workflow-edit-form"
                        onSubmit={handleSubmit}
                        className="flex flex-col h-full overflow-hidden"
                      >
                        <ExperimentSettingsFormContent
                          optimizationContentType={optimizationContentType}
                          optimizationSettings={optimizationSettings}
                          projectSplitCalculatorConfiguration={
                            projectSplitCalculatorConfiguration
                          }
                        />
                      </form>
                    )}
                  />
                </div>
              ),
            }[state]
          }
        </>
      )}
    </>
  )
}

export default ExperimentSettings
